import { useCallback, Fragment } from 'react'
import { useSelector } from 'react-redux'
import DriveSidebar from 'components/layout/DriveSidebar'
import Page from 'rui/widgets/Page'
import Panel, { PanelTitle } from 'components/layout/Panel'
import earningsStatementImage from 'images/earnings-statement-icon.png'
import { api, Container, Text, Spacer, Button, Box } from 'roadie-ui'
import { AdaptiveExternalLink } from 'components/layout/links'
import { isWebviewSelector } from 'selectors'
import { connect } from 'react-redux'
import Icon from 'components/misc/Icon'
import { LocalizedRichText, useLocalization, LocalizedText } from 'localization'

// TODO use webview prop instead and rename this component to EarningsStatementDownloadButton or something like that
const CheckIfWebview = connect(isWebviewSelector)((props) => {
  const { isWebview, handleDownload, itemId, fileType } = props

  const { mediaSize } = useSelector((state) => ({
    mediaSize: state.config.mediaSize,
  }))

  return isWebview ? (
    <AdaptiveExternalLink id="download-mileage" to={`download/${fileType}/${itemId}`}>
      <Icon symbol="external-link-active" />
    </AdaptiveExternalLink>
  ) : mediaSize === 'mobile' ? (
    <Icon symbol="arrow-down-active" onClick={handleDownload(itemId)} />
  ) : (
    <Button.Tertiary $color="primary-base" onClick={handleDownload(itemId)}>
      <LocalizedText translationKey="dweb_common_view" />
    </Button.Tertiary>
  )
})

const NoticePanel = ({ type }) => {
  const { t } = useLocalization()

  return (
    <Panel noPadding>
      <Container.Grid
        $gridTemplateColumns="max-content 1fr"
        $gridGap="1.8rem"
        $alignItems="start"
        $padding="1.8rem 2.4rem"
      >
        <Container.Base
          $backgroundColor="alert-base"
          $padding="2px 6px"
          $marginRight="2.4rem"
          $borderRadius="4px"
          $width="max-content"
          $height="max-content"
        >
          <Text.Caption $color="neutral-white" $letterSpacing="2px" $textAlign="left" $fontWeight="600">
            <LocalizedText translationKey="dweb_earnings_statement_notice" />
          </Text.Caption>
        </Container.Base>
        {type == 'prop22' ? (
          <Text.Body2 $textAlign="left" $color="neutral-black">
            <LocalizedText translationKey="dweb_earnings_statement_california_notice_body" />
          </Text.Body2>
        ) : (
          <Text.Body2 $textAlign="left" $color="neutral-black">
            <LocalizedRichText
              translationKey="dweb_earnings_statement_nyc_grocery_notice_body"
              params={{
                link1: (
                  <a
                    href="https://roadie-cdn.s3.us-east-1.amazonaws.com/nyc/NYC_NOR_ALL.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('dweb_earnings_statement_notice_of_rights_link_aria_label')}
                  >
                    <LocalizedText translationKey="dweb_common_here" />
                  </a>
                ),
              }}
            />
          </Text.Body2>
        )}
      </Container.Grid>
    </Panel>
  )
}

const Earning = ({ item }) => {
  const { formatStatementPeriodTitle } = useLocalization()
  const handleDownloadFile = useCallback(
    (id) => () => {
      api.downloadFile({
        path: `/my_profile/earnings_statements/${id}/download`,
        name: 'earnings.pdf',
      })
    },
    []
  )

  return (
    <Container.Base $padding="1.8rem 2.4rem">
      <Container.Grid $gridTemplateColumns="1fr max-content" $gridGap="0.8rem" $alignItems="center">
        <Text.Body2 $textAlign="left" $color="neutral-black">
          {formatStatementPeriodTitle(item.statement_period)}
        </Text.Body2>
        <CheckIfWebview handleDownload={handleDownloadFile} itemId={item.id} fileType="earning" />
      </Container.Grid>
    </Container.Base>
  )
}

function sortCb(itemA, itemB) {
  var dateA = new Date(itemA.statement_period.start_time).getTime()
  var dateB = new Date(itemB.statement_period.start_time).getTime()

  return dateA < dateB ? 1 : -1
}

const EarningsStatementsList = ({ title, items, noticeType }) => (
  <>
    <NoticePanel type={noticeType} />
    <Spacer $height="3.2rem" />
    <PanelTitle>{title}</PanelTitle>
    <Panel noPadding>
      <Box.Padding $padding="1.2rem 2.4rem">
        <Container.Base $display="flex" $justifyContent="space-between" $alignItems="center">
          <Text.Caption $fontWeight="500" $emphasis="medium" $textTransform="uppercase" $letterSpacing="2px">
            <LocalizedText translationKey="dweb_earnings_statement_period" />
          </Text.Caption>
        </Container.Base>
      </Box.Padding>
      <Box.Border $borderTopWidth="1px" $borderTopStyle="solid" $borderTopColor="neutral-light" />
      <Container.Base>
        {items.map((item, index) => (
          <Fragment key={`earning-${index}`}>
            <Earning item={item} />
            {index < items.length - 1 ? (
              <Box.Border
                $margin="0 0.8rem"
                $borderTopWidth="1px"
                $borderTopStyle="solid"
                $borderTopColor="neutral-light"
              />
            ) : null}
          </Fragment>
        ))}
      </Container.Base>
    </Panel>
  </>
)

const EarningsStatement = () => {
  const { t } = useLocalization()
  const { nycES, prop22ES } = useSelector(({ nycEarningsStatement = [], prop22EarningsStatement = [] }) => ({
    nycES: Array.isArray(nycEarningsStatement) && nycEarningsStatement.length ? nycEarningsStatement.sort(sortCb) : [],
    prop22ES:
      Array.isArray(prop22EarningsStatement) && prop22EarningsStatement.length
        ? prop22EarningsStatement.sort(sortCb)
        : [],
  }))

  // Create sections array and sort by most recent statement
  const sections = [
    prop22ES.length && {
      title: t('dweb_earnings_statement_california_earnings_statements'),
      items: prop22ES,
      noticeType: 'prop22',
      mostRecent: new Date(prop22ES[0].statement_period.start_time).getTime(),
    },
    nycES.length && {
      title: t('dweb_earnings_statement_nyc_grocery_earnings_statements'),
      items: nycES,
      noticeType: 'nyc',
      mostRecent: new Date(nycES[0].statement_period.start_time).getTime(),
    },
  ]
    .filter(Boolean)
    .sort((a, b) => b.mostRecent - a.mostRecent)

  return (
    <Page sidebar={<DriveSidebar />}>
      {sections.length > 0 ? (
        sections.map((section, index) => (
          <Fragment key={section.noticeType}>
            <EarningsStatementsList
              key={section.noticeType}
              title={section.title}
              items={section.items}
              noticeType={section.noticeType}
            />
            {index < sections.length - 1 && <Spacer $height="3.2rem" />}
          </Fragment>
        ))
      ) : (
        <>
          {/* Empty State */}
          <Panel>
            <Spacer $height=".8rem" />
            <img src={earningsStatementImage} style={{ height: '185px' }} alt={t('dweb_earnings_statement_title')} />
            <Spacer $height="4rem" />
            <Text.Subtitle1 $textAlign="left" $color="neutral-black">
              <LocalizedText translationKey="dweb_earnings_statement_no_earnings_statements_display" />
            </Text.Subtitle1>
            <Spacer $height="1.2rem" />
            <Text.Body2 $emphasis="medium" $textAlign="left">
              <LocalizedText translationKey="dweb_earnings_statement_if_eligible_earnings_statement_added_day_after_completion" />
            </Text.Body2>
            <Spacer $height="4.8rem" />
          </Panel>
        </>
      )}
    </Page>
  )
}

export default EarningsStatement
